import React from 'react';
import { Star, MessageSquare, Mic, CornerDownRight } from 'lucide-react';
import { formatFollowupValue } from '@/lib/choiceFollowups';
import AdminIdRow from '@/components/AdminIdRow';
import { STAR_COLORS } from '@/components/standard2026/Standard2026';
import { npsClassification } from '@/components/NpsFace';
import MatrixAnswerTable from '@/components/responseDetail/MatrixAnswerTable';
import GatedAudio from '@/components/responseDetail/GatedAudio';
import { questionLabel, readable, isVoiceAnswer, isFileList, voiceTranscript, commentOf } from '@/components/responseDetail/answerModel';

const BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
export const absUrl = (u) => (!u ? '' : /^https?:/i.test(u) ? u : `${BASE}${u}`);

const Stars = ({ value, max = 5, testId }) => {
  const n = Math.round(Number(value) || 0);
  return (
    <span className="inline-flex items-center gap-0.5" data-testid={testId} aria-label={`${n} out of ${max}`}>
      {Array.from({ length: max }, (_, i) => (
        <Star key={i} className="w-4 h-4" style={i < n ? { fill: STAR_COLORS[Math.min(n, 5)] || '#326941', color: STAR_COLORS[Math.min(n, 5)] || '#326941' } : { color: '#D0D5DB' }} />
      ))}
    </span>
  );
};

const Comment = ({ text, qid }) => text ? (
  <p className="text-xs text-[#64748B] italic mt-2 flex items-start gap-1.5" data-testid={`answer-comment-${qid}`}>
    <MessageSquare className="w-3 h-3 mt-0.5 shrink-0 text-[#94A3B8]" /><span>“{text}”</span>
  </p>
) : null;

// Conditional follow-up captured with a single-choice answer (stored separately).
export const FollowupLine = ({ fu, qid }) => fu && formatFollowupValue(fu) ? (
  <p className="text-sm text-[#0F172A] mt-1.5 flex items-start gap-1.5" data-testid={`answer-followup-${qid}`}>
    <CornerDownRight className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#94A3B8]" />
    <span><span className="text-xs font-semibold text-[#64748B] mr-1.5">{fu.label}</span><span data-testid={`answer-followup-value-${qid}`}>{formatFollowupValue(fu)}</span></span>
  </p>
) : null;

export function FeedbackItem({ item, responseId, derived, warnAudio }) {
  const { qid, q, val } = item;
  const voice = isVoiceAnswer(val);
  const transcript = voice ? voiceTranscript(val, derived) : null;
  const text = voice ? null : readable(val);
  return (
    <div className="py-4 first:pt-0 border-b border-[#F1F5F9] last:border-0 last:pb-0" data-testid={`answer-row-${qid}`}>
      <p className="text-xs font-semibold text-[#64748B] mb-2 flex items-center gap-2">
        {questionLabel(q)}
        {voice && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#214A7B] bg-[#E6EDF7] rounded-full px-2 py-0.5"><Mic className="w-3 h-3" /> Voice</span>}
      </p>
      <AdminIdRow questionId={qid} responseId={responseId} className="mb-1.5 -mt-1" testId={`detail-admin-id-${qid}`} />
      {voice ? (
        <div className="space-y-2">
          {val.audio_url && <GatedAudio src={absUrl(val.audio_url)} warn={!!warnAudio} testId={`answer-audio-${qid}`} />}
          {transcript
            ? <p className="text-[15px] leading-relaxed text-[#0F172A] whitespace-pre-wrap" data-testid={`answer-transcript-${qid}`}><span className="text-xs font-semibold text-[#64748B] mr-1.5">Transcript</span>{transcript}</p>
            : <p className="text-sm italic text-[#94A3B8]" data-testid={`answer-transcript-${qid}`}>Transcript not available yet.</p>}
        </div>
      ) : (
        <p className="text-[15px] leading-relaxed text-[#0F172A] whitespace-pre-wrap break-words" data-testid={`answer-text-${qid}`}>{text}</p>
      )}
      <Comment text={commentOf(val)} qid={qid} />
    </div>
  );
}

export function RatingItem({ item }) {
  const { qid, q, val } = item;
  if (q.type === 'attribute_ratings' && val && typeof val === 'object') {
    const attrs = Array.isArray(q.attributes) && q.attributes.length ? q.attributes : Object.keys(val).map((id) => ({ id, label: id }));
    return (
      <div data-testid={`answer-row-${qid}`}>
        <p className="text-xs font-semibold text-[#64748B] mb-2">{questionLabel(q)}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
          {attrs.filter((a) => val[a.id] != null).map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-3 py-2 border-b border-[#F1F5F9]" data-testid={`attr-rating-${qid}-${a.id}`}>
              <span className="text-sm text-[#0F172A]">{a.label}</span>
              <span className="flex items-center gap-2 shrink-0">
                <Stars value={val[a.id]} />
                <span className="text-xs font-semibold text-[#334155] w-8 text-right">{Math.round(val[a.id])}/5</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  const num = typeof val === 'object' && val ? (val.value ?? val.score_value) : val;
  const max = Number(q.star_settings?.max_stars || q.rating_scale?.max || 5);
  return (
    <div className="flex items-center justify-between gap-3 py-2" data-testid={`answer-row-${qid}`}>
      <span className="text-sm text-[#0F172A]">{questionLabel(q)}</span>
      {typeof num === 'number' && max <= 10
        ? <span className="flex items-center gap-2"><Stars value={num} max={Math.min(max, 10)} /><span className="text-xs font-semibold text-[#334155]">{num}/{max}</span></span>
        : <span className="text-sm text-[#0F172A]">{readable(val)}</span>}
    </div>
  );
}

function FileList({ files, qid }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`answer-files-${qid}`}>
      {files.map((f, i) => {
        const url = absUrl(f.url);
        const isImg = (f.type || '').startsWith('image/') || /\.(jpg|jpeg|png|webp)$/i.test(url);
        return (
          <div key={i} className="border border-[#F1F5F9] rounded-lg p-2 bg-[#F8FAFC]" data-testid={`answer-file-${qid}-${i}`}>
            {isImg
              ? <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={f.name || ''} className="max-h-40 rounded" /></a>
              : <a href={url} target="_blank" rel="noreferrer" className="text-xs text-[#1A3E6F] underline">{f.name || 'View file'}</a>}
            {f.comment && <p className="text-xs text-[#334155] mt-2 whitespace-pre-wrap">{f.comment}</p>}
          </div>
        );
      })}
    </div>
  );
}

export function GenericItem({ item, responseId }) {
  const { qid, q, val } = item;
  let body;
  if (isFileList(val)) body = <FileList files={val} qid={qid} />;
  else if (val && typeof val === 'object' && val.type === 'matrix' && Array.isArray(val.items)) body = <MatrixAnswerTable matrix={val} questionDef={q} />;
  else if (q.type === 'nps') body = <p className="text-sm text-[#0F172A]">{val} · {npsClassification(val)}</p>;
  else body = <p className="text-sm text-[#0F172A] break-words">{readable(val) || <span className="italic text-[#94A3B8]">(empty)</span>}</p>;
  return (
    <div className="py-3 first:pt-0 border-b border-[#F1F5F9] last:border-0 last:pb-0" data-testid={`answer-row-${qid}`}>
      <p className="text-xs font-semibold text-[#64748B] mb-1">{questionLabel(q)}</p>
      <AdminIdRow questionId={qid} responseId={responseId} className="mb-1.5 -mt-0.5" testId={`detail-admin-id-${qid}`} />
      {body}
      <FollowupLine fu={item.followup} qid={qid} />
      <Comment text={commentOf(val)} qid={qid} />
    </div>
  );
}

export { Stars };
